import { catchError, map, Observable, throwError } from 'rxjs';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { BOKKZ_BASE_URL, CorporateTaxEntity, EnrichAangifte, IncomeTaxEntity, JaarrekeningEntity } from './api.bokkz.services';
import { Accountantsverklaring, EnrichAccountantsverklaringRequest, EnrichGrondslagByIdResult, EnrichGrondslagenRequest, EnrichGrondslagenResponse, Jaarrapportage, JaarrapportageEntity } from './api.bokkz.jaarstukken';
import { USE_HTTP_ERROR_INTERCEPTOR, USE_HTTP_LOGGING_INTERCEPTOR } from './http-debug-interceptors';

export interface BalanceProviderInfo {
    id: string;
    name: string;
    supportsMultipleEnvironments: boolean;
    requiresEnvironmentSelection: boolean;
    supportsMultipleAdministrations: boolean;
    requiresAdministrationSelection: boolean;
    supportsBalanceByDateRange: boolean;
    supportsBalanceByPeriod: boolean;
}

export interface IdentityProviderInfo {
    id: string;
    name: string;
    supportsAuthorize: boolean;
    supportsAccessToken: boolean;
    requiresEnvironment: boolean;
    supportsRefreshToken: boolean;
    supportsTokenIntrospection: boolean;
    supportsEndSession: boolean;
}

export interface IdentityProviderStatus {
    id: string;
    name: string;
    environmentId: string;
    scope: string;
    authenticated: boolean;
    expiresAt: Date;
}

export interface AuthorizeRequest {
    uri: string;
}

export interface EnvironmentInfo {
    id: string;
    name: string;
}

export interface AdministrationInfo {
    id: string;
    name: string;
}

interface ProblemDetails {
    type: string;
    title: string;
    status: number;
    detail: string;
    instance: string;
}

export interface DebugOptions {
    showElementStyles: boolean;
    showSbrId: boolean;
    showJrElement: boolean;
    showNotInKvK: boolean;
    showMappingNotFound: boolean;
}

export interface RenderRequest {
  requestedReport: string;
  licensee: string;
  preparedBy: string;
  outputFormat: string;
  outputLanguage: string;
  stylesheet: string;
  copies: number;
  financialStatement: Jaarrapportage;
  accountantsverklaring: Accountantsverklaring;
  debugOptions: DebugOptions;
}

export interface RenderResponse {
    statusCode: number;
    statusMessage: string;
    fileName: string;
    base64File: string;
}

@Injectable({
    providedIn: 'root',
})
export class PmClient {
    private http: HttpClient;
    private readonly baseUrl: string;

    private readonly context = new HttpContext().set(USE_HTTP_LOGGING_INTERCEPTOR, true).set(USE_HTTP_ERROR_INTERCEPTOR, true);

    constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(BOKKZ_BASE_URL) baseUrl?: string) {
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
    }

    // Balance Provider Service

    getBalanceProviders$(): Observable<BalanceProviderInfo[]> {
        const url = `${this.baseUrl}/api/v1/sst/balanceproviders`;

        return this.http
            .get<BalanceProviderInfo[]>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    getBalanceProvider$(balanceProviderId: string): Observable<BalanceProviderInfo> {
        this.throwIfNullOrEmpty(balanceProviderId, 'balanceProviderId');

        const url = `${this.baseUrl}/api/v1/sst/balanceproviders/${encodeURIComponent(balanceProviderId)}`;

        return this.http
            .get<BalanceProviderInfo>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    getBalanceProviderEnvironments$(balanceProviderId: string): Observable<EnvironmentInfo[]> {
        this.throwIfNullOrEmpty(balanceProviderId, 'balanceProviderId');

        const url = `${this.baseUrl}/api/v1/sst/balanceproviders/${encodeURIComponent(balanceProviderId)}/environments`;

        return this.http
            .get<EnvironmentInfo[]>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    getBalanceProviderAdministrations$(balanceProviderId: string): Observable<BalanceProviderInfo[]> {
        this.throwIfNullOrEmpty(balanceProviderId, 'balanceProviderId');

        const url = `${this.baseUrl}/api/v1/sst/balanceproviders/${encodeURIComponent(balanceProviderId)}/administrations`;

        return this.http
            .get<BalanceProviderInfo[]>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    getBalanceProviderAdministration$(balanceProviderId: string, administrationId: string): Observable<BalanceProviderInfo[]> {
        this.throwIfNullOrEmpty(balanceProviderId, 'balanceProviderId');
        this.throwIfNullOrEmpty(administrationId, 'administrationId');

        const url = `${this.baseUrl}/api/v1/sst/balanceproviders/${encodeURIComponent(balanceProviderId)}/administrations/${encodeURIComponent(administrationId)}`;

        return this.http
            .get<BalanceProviderInfo[]>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    getBalanceProviderEnvironmentAdministrations$(balanceProviderId: string, environmentId: string): Observable<AdministrationInfo[]> {
        this.throwIfNullOrEmpty(balanceProviderId, 'balanceProviderId');
        this.throwIfNullOrEmpty(environmentId, 'environmentId');

        const url = `${this.baseUrl}/api/v1/sst/balanceproviders/${encodeURIComponent(balanceProviderId)}/environments/${encodeURIComponent(environmentId)}/administrations`;

        return this.http
            .get<AdministrationInfo[]>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    getBalanceProviderEnvironmentAdministration$(balanceProviderId: string, environmentId: string, administrationId: string): Observable<BalanceProviderInfo[]> {
        this.throwIfNullOrEmpty(balanceProviderId, 'balanceProviderId');
        this.throwIfNullOrEmpty(environmentId, 'environmentId');
        this.throwIfNullOrEmpty(administrationId, 'administrationId');

        const url = `${this.baseUrl}/api/v1/sst/balanceproviders/${encodeURIComponent(balanceProviderId)}/environments/${encodeURIComponent(environmentId)}/administrations/${encodeURIComponent(administrationId)}`;

        return this.http
            .get<BalanceProviderInfo[]>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    // Identity Provider Service

    getIdentityProvider$(identityProviderId: string): Observable<IdentityProviderInfo> {
        this.throwIfNullOrEmpty(identityProviderId, 'identityProviderId');

        const url = `${this.baseUrl}/api/v1/sst/identityproviders/${encodeURIComponent(identityProviderId)}`;

        return this.http
            .get<IdentityProviderInfo>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    getIdentityProviderStatus$(identityProviderId: string): Observable<IdentityProviderStatus> {
        this.throwIfNullOrEmpty(identityProviderId, 'identityProviderId');

        const url = `${this.baseUrl}/api/v1/sst/identityproviders/${encodeURIComponent(identityProviderId)}/status`;

        return this.http
            .get<IdentityProviderStatus>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    getIdentityProviderEnvironmentStatus$(identityProviderId: string, environmentId: string): Observable<IdentityProviderStatus> {
        this.throwIfNullOrEmpty(identityProviderId, 'identityProviderId');
        this.throwIfNullOrEmpty(environmentId, 'environmentId');

        const url = `${this.baseUrl}/api/v1/sst/identityproviders/${encodeURIComponent(identityProviderId)}/environments/${encodeURIComponent(environmentId)}/status`;

        return this.http
            .get<IdentityProviderStatus>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    getIdentityProviderAuthorizeRequest$(identityProviderId: string): Observable<AuthorizeRequest> {
        this.throwIfNullOrEmpty(identityProviderId, 'identityProviderId');

        const url = `${this.baseUrl}/api/v1/sst/identityproviders/${encodeURIComponent(identityProviderId)}/authorize/`;

        return this.http
            .get<AuthorizeRequest>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    // TODO: Identity Provider Authorization Callback heeft meer parameters dan alleen code en state
    handleIdentityProviderAuthorizeCallback$(identityProviderId: string, code: string, state: string): Observable<void> {
        this.throwIfNullOrEmpty(identityProviderId, 'identityProviderId');
        this.throwIfNullOrEmpty(code, 'code');
        this.throwIfNullOrEmpty(state, 'state');

        const url = `${this.baseUrl}/api/v1/sst/identityprovider/${encodeURIComponent(identityProviderId)}/authorize/callback/code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;

        return this.http
            .get<void>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    postIdentityProviderAuthorizeEnvironment$(identityProviderId: string, environmentId: string, accessToken: string): Observable<void> {
        this.throwIfNullOrEmpty(identityProviderId, 'identityProviderId');
        this.throwIfNullOrEmpty(environmentId, 'environmentId');
        this.throwIfNullOrEmpty(accessToken, 'accessToken');

        const url = `${this.baseUrl}/api/v1/sst/identityproviders/${encodeURIComponent(identityProviderId)}/environments/${encodeURIComponent(environmentId)}/authorize`;

        const body = new HttpParams().set('accessToken', accessToken!);

        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
        });

        return this.http
            .post<void>(url, body.toString(), { headers: headers, context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    postIdentityProviderEndSession$(identityProviderId: string): Observable<void> {
        this.throwIfNullOrEmpty(identityProviderId, 'identityProviderId');

        const url = `${this.baseUrl}/api/v1/sst/identityproviders/${encodeURIComponent(identityProviderId)}/endsession`;

        return this.http
            .post<void>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    postIdentityProviderEnvironmentEndSession$(identityProviderId: string, environmentId: string): Observable<void> {
        this.throwIfNullOrEmpty(identityProviderId, 'identityProviderId');
        this.throwIfNullOrEmpty(environmentId, 'environmentId');

        const url = `${this.baseUrl}/api/v1/sst/identityproviders/${encodeURIComponent(identityProviderId)}/environments/${encodeURIComponent(environmentId)}/endsession`;

        return this.http
            .post<void>(url, { context: this.context })
            .pipe(catchError((error: any) => this.handleProblemDetails(error)));
    }

    private handleError(error: HttpErrorResponse): Observable<never> {
        if (error.error instanceof ErrorEvent) {
            // Client-side or network error occurred
            console.error('An error occurred:', error.error.message);
        } else {
            // Backend returned an unsuccessful response code
            console.error(`Backend returned code ${error.status}, body was: `, error.error);
        }
        // Return a thrown observable with a user-friendly error message
        return throwError(() => new Error('Something went wrong. Please try again later.'));
    }

    private handleProblemDetails(error: HttpErrorResponse): Observable<never> {
        if (typeof error.error === 'string' && error.error.length > 0) {
            const problemDetails = JSON.parse(error.error) as ProblemDetails;

            if (!!problemDetails) {
                throwError(() => new Error(`${problemDetails.detail} (Status: ${problemDetails.status})`));
            }
        }

        return throwError(() => new Error('Something went wrong. Please try again later.'));
    }

    private throwIfNullOrEmpty(value: string | null | undefined, argumentName: string): void {
        if (!value || value.trim().length === 0) {
            throw new Error(`Argument '${argumentName}' cannot be null, undefined or empty`);
        }
    }

    postBalanceProviderBalances(
        clientId: string,
        balanceProviderId: string,
        administrationId: string,
        partnerBoolean: boolean,
        tax: IncomeTaxEntity | CorporateTaxEntity | JaarrekeningEntity,
    ) {
        if (clientId === undefined || clientId === null) {
            throw new Error("The parameter 'clientId' must be defined.");
        }
        if (balanceProviderId === undefined || balanceProviderId === null) {
            throw new Error("The parameter 'balanceProviderId' must be defined.");
        }
        if (administrationId === undefined || administrationId === null) {
            throw new Error("The parameter 'administrationId' must be defined.");
        }

        let url_ =
            this.baseUrl +
            '/api/v1/sst/balanceproviders/{clientId}/0/{balanceProviderId}/administrations/{administrationId}/periode-balances';
        url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
        url_ = url_.replace('{balanceProviderId}', encodeURIComponent('' + balanceProviderId));
        url_ = url_.replace('{administrationId}', encodeURIComponent('' + administrationId));

        const bsnIb = partnerBoolean ? tax.aangifte?.partnerAangifte.aangever?.bsn : tax.aangifte?.aangever?.bsn;

        const enrichAangifte = {} as EnrichAangifte;
        enrichAangifte.aangifte = JSON.stringify(tax);
        enrichAangifte.bsn = bsnIb;

        const options_ = {
            body: enrichAangifte,
            observe: 'response' as const,
            responseType: 'json' as const,
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
            }),
            context: this.context,
        };

        return this.http.request('POST', url_, options_).pipe(
            catchError((error: any) => this.handleError(error)),
            map((response) => response.body as IncomeTaxEntity | CorporateTaxEntity | JaarrapportageEntity),
        );
    }

    postBalanceProviderBalancesEnvironment(
        clientId: string,
        balanceProviderId: string,
        environmentId: string,
        administrationId: string,
        partnerBoolean: boolean,
        tax: IncomeTaxEntity | CorporateTaxEntity | JaarrekeningEntity,
    ) {
        if (clientId === undefined || clientId === null) {
            throw new Error("The parameter 'clientId' must be defined.");
        }
        if (balanceProviderId === undefined || balanceProviderId === null) {
            throw new Error("The parameter 'balanceProviderId' must be defined.");
        }
        if (environmentId === undefined || environmentId === null) {
            throw new Error("The parameter 'environmentId' must be defined.");
        }
        if (administrationId === undefined || administrationId === null) {
            throw new Error("The parameter 'administrationId' must be defined.");
        }

        let url_ =
            this.baseUrl +
            '/api/v1/sst/balanceproviders/{clientId}/0/{balanceProviderId}/environments/{environmentId}/administrations/{administrationId}/periode-balances';
        url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
        url_ = url_.replace('{balanceProviderId}', encodeURIComponent('' + balanceProviderId));
        url_ = url_.replace('{environmentId}', encodeURIComponent('' + environmentId));
        url_ = url_.replace('{administrationId}', encodeURIComponent('' + administrationId));

        const bsnIb = partnerBoolean ? tax.aangifte?.partnerAangifte.aangever?.bsn : tax.aangifte?.aangever?.bsn;

        const enrichAangifte = {} as EnrichAangifte;
        enrichAangifte.aangifte = JSON.stringify(tax);
        enrichAangifte.bsn = bsnIb;

        const options_ = {
            body: enrichAangifte,
            observe: 'response' as const,
            responseType: 'json' as const,
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
            }),
            context: this.context,
        };

        return this.http.request('POST', url_, options_).pipe(
            catchError((error: any) => this.handleError(error)),
            map((response) => response.body as IncomeTaxEntity | CorporateTaxEntity | JaarrapportageEntity),
        );
    }

    postPmRenderRequestJaarrapportage(renderRequest: RenderRequest): Observable<RenderResponse> {
        let url_ = this.baseUrl + '/api/v1/sst/render/renderreport';

        const options_ = {
            body: renderRequest,
            observe: 'response' as const,
            responseType: 'json' as const,
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
            }),
            context: this.context,
        };

        return this.http.request('POST', url_, options_).pipe(
            catchError((error: any) => this.handleError(error)),
            map((response) => response.body as RenderResponse),
        );
    }

    postPmRenderRequestPublicatiestukken(renderRequest: RenderRequest): Observable<RenderResponse> {
        let url_ = this.baseUrl + '/api/v1/sst/render/renderpubs';

    url_ = url_.replace(/[?&]$/, '');

    return this.http.request('POST', url_, {
      body: renderRequest,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as RenderResponse),
    );
  }

}

@Injectable({
  providedIn: 'root',
})
export class EnrichClient {
  private http: HttpClient;
  private readonly baseUrl: string;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(BOKKZ_BASE_URL) baseUrl?: string,
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  getEnrichedGrondslagen(request: EnrichGrondslagenRequest): Observable<EnrichGrondslagenResponse> {
    let url_ = this.baseUrl + '/api/v1/jr/enrich-grondslagen';

    url_ = url_.replace(/[?&]$/, '');

    return this.http.request('POST', url_, {
      body: request,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as EnrichGrondslagenResponse),
    );
  }

  getEnrichedGrondslagenByIds(request: EnrichGrondslagenRequest): Observable<EnrichGrondslagByIdResult[]> {
    let url_ = this.baseUrl + '/api/v1/jr/enrich-grondslagen-by-ids';

    url_ = url_.replace(/[?&]$/, '');

    return this.http.request('POST', url_, {
      body: request,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as EnrichGrondslagByIdResult[]),
    );
  }

  getEnrichedAccountantsverklaring(request: EnrichAccountantsverklaringRequest): Observable<Accountantsverklaring> {
    let url_ = this.baseUrl + '/api/v1/jr/enrich-accountantsverklaring';

    url_ = url_.replace(/[?&]$/, '');

    return this.http.request('POST', url_, {
      body: request,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as Accountantsverklaring),
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      // Client-side or network error occurred
      console.error('An error occurred:', error.error.message);
    } else {
      // Backend returned an unsuccessful response code
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return a thrown observable with a user-friendly error message
    return throwError(() => new Error('Something went wrong. Please try again later.'));
  }

}
